/** Shared drag/drop + file ingest helpers for sampler UI. */

import type { SamplerPreset } from "../../../../src/lib/audio";

/** Library sample id payload (Samples panel → sampler waveform / author). */
export const ADAM_SAMPLE_MIME = "application/x-adam-sample-id";

/** Samples panel folder drag (reparent / reorder within library). */
export const ADAM_FOLDER_MIME = "application/x-adam-folder-id";

function typesList(event: { dataTransfer?: DataTransfer | null }): string[] {
  const types = event.dataTransfer?.types;
  if (!types) {
    return [];
  }
  return Array.from(types as ArrayLike<string>);
}

function hasMime(list: string[], mime: string): boolean {
  // Chromium lowercases custom MIME types in `types` during dragover.
  return list.includes(mime) || list.includes(mime.toLowerCase());
}

export function isAudioFileDrag(event: { dataTransfer?: DataTransfer | null }): boolean {
  return typesList(event).includes("Files");
}

export function isLibrarySampleDrag(event: { dataTransfer?: DataTransfer | null }): boolean {
  return hasMime(typesList(event), ADAM_SAMPLE_MIME);
}

export function isLibraryFolderDrag(event: { dataTransfer?: DataTransfer | null }): boolean {
  return hasMime(typesList(event), ADAM_FOLDER_MIME);
}

/** True when the drag can assign audio into a sampler (OS file or library sample). */
export function isSamplerSampleDrag(event: { dataTransfer?: DataTransfer | null }): boolean {
  return isAudioFileDrag(event) || isLibrarySampleDrag(event);
}

/**
 * Sample drag: `copy` into samplers / drum pads, `move` onto Sample Manager folders.
 */
export function setSampleDragData(
  dataTransfer: DataTransfer,
  sampleId: string,
  name?: string,
): void {
  dataTransfer.setData(ADAM_SAMPLE_MIME, sampleId);
  dataTransfer.setData("text/plain", name?.trim() || sampleId);
  dataTransfer.effectAllowed = "copyMove";
}

export function setFolderDragData(
  dataTransfer: DataTransfer,
  folderId: string,
  name?: string,
): void {
  dataTransfer.setData(ADAM_FOLDER_MIME, folderId);
  dataTransfer.setData("text/plain", name?.trim() || folderId);
  dataTransfer.effectAllowed = "move";
}

export function sampleIdFromDrop(event: { dataTransfer?: DataTransfer | null }): string | null {
  const dt = event.dataTransfer;
  if (!dt) {
    return null;
  }
  const raw =
    dt.getData(ADAM_SAMPLE_MIME)
    || dt.getData(ADAM_SAMPLE_MIME.toLowerCase())
    || "";
  const id = raw.trim();
  return id || null;
}

export function folderIdFromDrop(event: { dataTransfer?: DataTransfer | null }): string | null {
  const dt = event.dataTransfer;
  if (!dt) {
    return null;
  }
  const raw =
    dt.getData(ADAM_FOLDER_MIME)
    || dt.getData(ADAM_FOLDER_MIME.toLowerCase())
    || "";
  const id = raw.trim();
  return id || null;
}

export function fileFromDrop(event: { dataTransfer?: DataTransfer | null }): File | null {
  return audioFilesFromDrop(event)[0] ?? null;
}

/** True when `file` looks like importable audio (MIME or extension). */
export function isAudioFileLike(file: File): boolean {
  return file.type.startsWith("audio/")
    || /\.(wav|mp3|ogg|flac|aif|aiff|m4a)$/i.test(file.name);
}

/** All dropped audio files (preserves order; skips non-audio). */
export function audioFilesFromDrop(event: { dataTransfer?: DataTransfer | null }): File[] {
  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) {
    return [];
  }
  const out: File[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i]!;
    if (isAudioFileLike(file)) {
      out.push(file);
    }
  }
  return out;
}

/**
 * Patch shape used when picking a catalog sample from the select or dropping
 * one from the Samples panel onto a sampler.
 */
export function samplerPresetFromSampleId(
  preset: SamplerPreset,
  sampleId: string,
): Partial<SamplerPreset> {
  return {
    primarySampleId: sampleId,
    zones: preset.zones.map((zone, i) =>
      i === 0 ? { ...zone, sampleId } : zone,
    ),
    region: { start: 0, end: 0 },
    loop: {
      ...preset.loop,
      enabled: sampleId.startsWith("factory/") ? true : preset.loop.enabled,
      mode: sampleId.startsWith("factory/")
        ? "continuous"
        : (preset.loop.mode ?? "untilRelease"),
    },
  };
}
